import {
  Component,
  signal,
  computed,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  ElementRef,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GeographyService } from '../../../../../core/services/geography.service';
import { ProfessionalsService } from '../../../../../core/services/professionals.service';
import { UserSessionService } from '../../../../../core/services/mock-auth.service';
import { ProfessionalsWizardService } from '../../../../services/professionals-wizard.service';
import { ProfessionalsDataService } from '../../../../services/professionals-data.service';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { FileUploadModule } from 'primeng/fileupload';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { hasMeaningfulText } from '../../../../../core/validators/text-value.utils';
import { ValidationErrorSection, validationSectionsFromMessages } from '../../../validation-error-summary/validation-error-summary.model';
import { OperationalSitesSectionComponent } from '../operational-sites/operational-sites-section.component';
import {
  OperatingSiteVM,
  sitesFromResponse,
  sitesToRequest,
  validateOperationalSites,
} from '../operational-sites/operational-sites.model';

interface DropdownOption {
  label: string;
  value: string;
}

interface Collaborator {
  id: number;
  surname: string;
  name: string;
  fiscalCode: string;
  registrationNumber: string;
  claimsSystemAccess: boolean;
}

@Component({
  selector: 'app-auto-expert-form',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    AccordionModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    CheckboxModule,
    RadioButtonModule,
    FileUploadModule,
    ToastModule,
    TooltipModule,
    OperationalSitesSectionComponent,
  ],
  templateUrl: './auto-expert-form.component.html',
  styleUrl: './shared-form-styles.scss',
})
export class AutoExpertFormComponent implements OnInit {
  @ViewChildren('firstInput') firstInputs!: QueryList<ElementRef>;

  readonly hasMeaningfulText = hasMeaningfulText;
  readonly today = new Date();

  // Optional professional ID from parent component (for review page)
  professionalIdInput = input<string | null>(null);
  // Explicit readonly flag passed by parent
  readonly = input<boolean>(false);

  @Output() onSave = new EventEmitter<any>();
  @Output() onSaveDraft = new EventEmitter<any>();
  @Output() validationFailed = new EventEmitter<ValidationErrorSection[]>();

  private geographyService = inject(GeographyService);
  private professionalsService = inject(ProfessionalsService);
  private mockAuthService = inject(UserSessionService);
  private titleService = inject(Title);
  private messageService = inject(MessageService);
  private professionalsWizardService = inject(ProfessionalsWizardService);
  private professionalsDataService = inject(ProfessionalsDataService);

  // Live region message for screen readers
  liveRegionMessage = signal<string>('');

  // Computed: verifica se siamo in modalità read-only
  isReadOnly = computed(() => {
    return this.professionalsDataService.isReadOnly() || this.readonly();
  });

  // Form submission tracking
  formSubmitted = signal<boolean>(false);
  private touchedFields = new Set<string>();

  // Section 1: Specific Information (all required)
  activityStartDate = signal<Date | null>(null);
  nationalRegistrationNumber = signal<string>('');
  companiesWorkedFor = signal<string>('');
  operatingTerritories = signal<string[]>([]);

  // Section 2: Sede operativa (multi-sede, gestita da app-operational-sites-section)
  operatingSameAsLegal = signal<boolean>(true);
  // Id della sede "legalFallback" persistita (round-trip per preservare i codici SOL associati)
  operatingLegalSiteId = signal<number | null>(null);
  operatingSites = signal<OperatingSiteVM[]>([]);

  // Section 3: Collaborators
  collaborators = signal<Collaborator[]>([]);
  private collaboratorIdCounter = 1;

  // Dropdown options
  provinceOptions = signal<DropdownOption[]>([]);

  // Readonly: etichette dei territori operativi selezionati, risolte dalle
  // province. Serve a mostrare i valori in sola lettura senza dipendere dal
  // rendering del p-multiselect disabilitato (che non mostra i chip).
  operatingTerritoriesLabels = computed(() => {
    const options = this.provinceOptions();
    return this.operatingTerritories().map((value) => {
      const match = options.find((option) => option.value === value);
      return match?.label ?? value;
    });
  });

  ngOnInit(): void {
    this.titleService.setTitle('Portale Fiduciari - Dati Specifici Perito Auto');
    this.loadProvinceOptions();
    this.loadProfessionalSpecificData();
  }

  private loadProfessionalSpecificData(): void {
    const professionalId = this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId();
    
    if (!professionalId) {
      console.warn('Professional ID not found');
      return;
    }

    this.professionalsService.getAutoExpertData(professionalId).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.populateFormData(response.data);
        } else {
          // No data returned - mark step as NOT completed
          console.warn('⚠️ No auto expert data returned from API');
          this.professionalsWizardService.markStepCompleted(1, false);
          this.professionalsWizardService.saveState();
        }
      },
      error: (error) => {
        console.error('Error loading specific data:', error);
        // Mark step as NOT completed on error
        this.professionalsWizardService.markStepCompleted(1, false);
        this.professionalsWizardService.saveState();
        // Don't show error toast on first load - data might not exist yet
      }
    });
  }

  private populateFormData(data: any): void {
    // Extract from specificData wrapper
    const specificData = data.specificData || data;

    // Activity start date
    if (specificData.activityStartDate) {
      this.activityStartDate.set(new Date(specificData.activityStartDate));
    }

    // Registration number (backend: professionalRegistrationNumber)
    this.nationalRegistrationNumber.set(specificData.professionalRegistrationNumber || '');

    // Operating territories (backend: operatingTerritories array)
    if (Array.isArray(specificData.operatingTerritories)) {
      this.operatingTerritories.set(specificData.operatingTerritories);
    } else {
      this.operatingTerritories.set([]);
    }

    // Companies worked for
    this.companiesWorkedFor.set(specificData.assignedCompanies ?? '');

    // Sede operativa (backend: operationalSites[] multi-sede con flag legalFallback)
    const loadedSites = sitesFromResponse(specificData.operationalSites);
    this.operatingSameAsLegal.set(loadedSites.sameAsLegal);
    this.operatingLegalSiteId.set(loadedSites.legalSiteId);
    this.operatingSites.set(loadedSites.sites);

    // Collaborators (at root level, not in specificData)
    const collaborators = data.collaborators || [];
    if (collaborators.length > 0) {
      const collabs = collaborators.map((c: any, index: number) => ({
        id: index,
        surname: c.lastName || '',
        name: c.firstName || '',
        fiscalCode: c.fiscalCode || '',
        registrationNumber: c.professionalRegistrationNumber || '',
        claimsSystemAccess: c.needsClaimsPortalAccess || false,
      }));
      this.collaborators.set(collabs);
      this.collaboratorIdCounter = collabs.length;
    }

    // Check section status and mark step as completed ONLY if FILLED
    if ((specificData as any).specificDataSectionStatus === 'FILLED') {
      console.log('✅ Specific data section is FILLED - marking step as completed');
      this.professionalsWizardService.markStepCompleted(1, true);
    } else {
      console.log('⚠️ Specific data section is NOT filled (status:', (specificData as any).specificDataSectionStatus, ') - marking step as NOT completed');
      this.professionalsWizardService.markStepCompleted(1, false);
    }
    
    // Save wizard state to persist the completed status
    this.professionalsWizardService.saveState();
  }

  private loadProvinceOptions(): void {
    this.geographyService
      .getProvinces()
      .pipe(catchError(() => of([])))
      .subscribe((provinces) => {
        this.provinceOptions.set(
          (provinces || []).map((item) => ({
            label: item.name,
            value: item.code,
          })),
        );
      });
  }

  createEmptyCollaborator(): Collaborator {
    return {
      id: this.collaboratorIdCounter++,
      surname: '',
      name: '',
      fiscalCode: '',
      registrationNumber: '',
      claimsSystemAccess: false,
    };
  }

  addCollaborator(): void {
    this.collaborators.update((curr) => [...curr, this.createEmptyCollaborator()]);

    // Announce to screen readers
    const count = this.collaborators().length;
    this.liveRegionMessage.set(`Collaboratore ${count} aggiunto`);
    setTimeout(() => this.liveRegionMessage.set(''), 1000);

    // Focus management: Move focus to the new collaborator's first input
    setTimeout(() => {
      const inputs = this.firstInputs.toArray();
      const newInput = inputs[inputs.length - 1];
      if (newInput) {
        newInput.nativeElement.focus();
      }
    }, 100);
  }

  removeCollaborator(id: number): void {
    const index = this.collaborators().findIndex((c) => c.id === id);
    this.collaborators.update((curr) => curr.filter((c) => c.id !== id));

      // Announce to screen readers
      this.liveRegionMessage.set(`Collaboratore ${index + 1} rimosso`);
      setTimeout(() => this.liveRegionMessage.set(''), 1000);

      // Focus management: Move focus to previous collaborator or add button
      setTimeout(() => {
        const inputs = this.firstInputs.toArray();
        const targetIndex = Math.max(0, index - 1);
        const targetInput = inputs[targetIndex];
        if (targetInput) {
          targetInput.nativeElement.focus();
        }
      }, 100);
  }

  isFieldTouched(fieldName: string): boolean {
    return this.formSubmitted() || this.touchedFields.has(fieldName);
  }

  markFieldTouched(fieldName: string): void {
    this.touchedFields.add(fieldName);
  }

  // Validation helpers
  isValidFiscalCode(fiscalCode: string): boolean {
    if (!fiscalCode) return false;
    // Italian fiscal code: 16 alphanumeric characters
    return /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i.test(fiscalCode);
  }

  // Check if a collaborator has at least one field filled (for conditional required)
  hasCollaboratorStarted(collab: any): boolean {
    return !!(
      this.hasMeaningfulText(collab.surname) || 
      this.hasMeaningfulText(collab.name) || 
      this.hasMeaningfulText(collab.fiscalCode) || 
      this.hasMeaningfulText(collab.registrationNumber)
    );
  }

  private validateForm(): string[] {
    const errors: string[] = [];

    // Section 1: Specific Information (all required)
    if (!this.activityStartDate()) {
      errors.push('Inizio Attività dal');
    }
    if (!this.nationalRegistrationNumber() || this.nationalRegistrationNumber().trim() === '') {
      errors.push('Numero iscrizione albo nazionale');
    }
    if (!this.companiesWorkedFor() || this.companiesWorkedFor().trim() === '') {
      errors.push('Imprese per le quali lavora');
    }
    if (!this.operatingTerritories() || this.operatingTerritories().length === 0) {
      errors.push('Territorio operativo');
    }

    // Sede operativa: flag "coincide con la legale" + N sedi (almeno 1 se non coincide)
    errors.push(...validateOperationalSites(this.operatingSameAsLegal(), this.operatingSites()));

    return errors;
  }

  private collectFormData(): any {
    const formatDateToISO = (date: Date | null): string => {
      if (!date) return '';
      return date.toISOString().split('T')[0];
    };

    return {
      specificData: {
        activityStartDate: formatDateToISO(this.activityStartDate()),
        professionalRegistrationNumber: this.nationalRegistrationNumber(),
        operatingTerritories: this.operatingTerritories(),
        assignedCompanies: this.companiesWorkedFor() || null,
        operationalSites: sitesToRequest(
          this.operatingSameAsLegal(),
          this.operatingLegalSiteId(),
          this.operatingSites(),
        ),
      },
    };
  }

  saveDraft(): void {
    const formData = this.collectFormData();
    const professionalId = this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Professional ID non disponibile. Effettua nuovamente il login.',
        life: 4000,
      });
      return;
    }

    // Add section status to specificData
    const payload = {
      ...formData,
      specificData: {
        ...formData.specificData,
        specificDataSectionStatus: 'DRAFT'
      }
    };

    console.log('Saving draft...', payload);

    this.professionalsService.updateAutoExpertData(professionalId, payload)
      .pipe(
        catchError((error) => {
          console.error('❌ Error saving draft:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile salvare la bozza. Riprova più tardi.',
            life: 4000,
          });
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          console.log('✅ Draft saved successfully:', response);

          // Emit to parent without marking as completed
          this.onSaveDraft.emit(formData);

          // Announce success
          this.liveRegionMessage.set('Bozza salvata con successo');
          setTimeout(() => this.liveRegionMessage.set(''), 3000);

          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Bozza salvata correttamente',
            life: 4000,
          });
        }
      });
  }

  saveAndContinue(): void {
    // Mark form as submitted to show inline validation errors
    this.formSubmitted.set(true);

    // Validate form before proceeding
    const validationErrors = this.validateForm();

    if (validationErrors.length > 0) {
      this.validationFailed.emit(validationSectionsFromMessages([
        { title: 'Dati specifici', errors: validationErrors.filter(error => !error.includes(':')) },
        { title: 'Sede operativa', errors: validationErrors.filter(error => error.startsWith('Sede operativa')) },
      ]));
      // Announce to screen readers
      this.liveRegionMessage.set('Compila tutti i campi obbligatori');
      setTimeout(() => this.liveRegionMessage.set(''), 3000);

      return;
    }

    const formData = this.collectFormData();
    const professionalId = this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Professional ID non disponibile. Effettua nuovamente il login.',
        life: 4000,
      });
      return;
    }

    // Add section status to specificData
    const payload = {
      ...formData,
      specificData: {
        ...formData.specificData,
        specificDataSectionStatus: 'FILLED'
      }
    };

    this.professionalsService.updateAutoExpertData(professionalId, payload)
      .pipe(
        catchError((error) => {
          console.error('❌ Error saving data:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile salvare i dati. Riprova più tardi.',
            life: 4000,
          });
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          console.log('✅ Data saved successfully:', response);

          // Emit to parent to mark step as completed and navigate
          this.onSave.emit(formData);

          // Announce success
          this.liveRegionMessage.set('Dati salvati con successo. Passaggio alla sezione successiva');

          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Dati salvati correttamente',
            life: 3000,
          });
        }
      });
  }
}